/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect } from 'react';
import { Code2, Check, RefreshCw, AlertCircle } from 'lucide-react';
import { useApp } from '../../contexts/AppContext';
import { SkillShopService } from '../../services/skillShopService';
import { CodingAgent } from '../../types';

interface AgentInstallationStatus {
  checked: boolean;
  installed: boolean;
  checking: boolean;
  command?: string;
  message?: string;
}

export function CodingAgentSettings() {
  const { codingAgent, updateCodingAgent } = useApp();
  const [skillShopService] = useState(() => new SkillShopService());

  const [agentInstallationStatus, setAgentInstallationStatus] = useState<Record<CodingAgent, AgentInstallationStatus>>({
    'claude-code': { checked: false, installed: false, checking: false },
    'cursor': { checked: false, installed: false, checking: false },
    'gemini': { checked: false, installed: false, checking: false }
  });

  const handleCodingAgentChange = (agent: CodingAgent) => {
    updateCodingAgent(agent);
  };

  const checkAgentInstallation = async (agent: CodingAgent) => {
    setAgentInstallationStatus(prev => ({
      ...prev,
      [agent]: { ...prev[agent], checking: true }
    }));

    try {
      const result = await skillShopService.checkCodingAgentInstallation(agent);
      
      setAgentInstallationStatus(prev => ({
        ...prev,
        [agent]: {
          checked: true,
          installed: result.installed,
          checking: false,
          command: result.command,
          message: result.message
        }
      }));
    } catch (error) {
      console.error(`Failed to check ${agent} installation:`, error);
      setAgentInstallationStatus(prev => ({
        ...prev,
        [agent]: {
          checked: true,
          installed: false,
          checking: false,
          message: 'Failed to check installation status'
        }
      }));
    }
  };

  // Check all agents on mount
  useEffect(() => {
    const agents: CodingAgent[] = ['claude-code', 'cursor', 'gemini'];
    agents.forEach(agent => {
      checkAgentInstallation(agent);
    });
  }, []);

  const agentNames: Record<CodingAgent, string> = {
    'claude-code': 'Claude Code',
    'cursor': 'Cursor',
    'gemini': 'Gemini CLI'
  };

  return (
    <div style={{ 
      marginBottom: '32px',
      padding: '20px',
      background: '#f9f9f9',
      borderRadius: '8px',
      border: '1px solid #ddd'
    }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Code2 size={20} style={{ marginRight: '8px', color: '#0066cc' }} />
        <h3 style={{ margin: 0 }}>Code Generation Settings</h3>
      </div>

      <div className="form-group">
        <label htmlFor="coding-agent">Coding Agent</label>
        <select
          id="coding-agent"
          value={codingAgent}
          onChange={(e) => handleCodingAgentChange(e.target.value as CodingAgent)}
          style={{
            width: '100%',
            padding: '8px 12px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            fontSize: '14px'
          }}
        >
          <option value="claude-code">Claude Code (Anthropic)</option>
          <option value="cursor">Cursor</option>
          <option value="gemini">Gemini CLI (Google)</option>
        </select>
        <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '8px' }}>
          Select which AI coding agent to use for code generation tasks. The selected agent must be installed and available in the system PATH.
        </small>
      </div>

      {/* Agent Installation Status */}
      <div style={{ 
        marginTop: '20px',
        padding: '16px',
        background: '#fff',
        borderRadius: '6px',
        border: '1px solid #e5e7eb'
      }}>
        <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#374151' }}>
          Agent Installation Status
        </h4>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {(['claude-code', 'cursor', 'gemini'] as CodingAgent[]).map(agent => {
            const status = agentInstallationStatus[agent];
            
            return (
              <div key={agent} style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                padding: '8px 12px',
                background: '#f9fafb',
                borderRadius: '4px',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '14px', fontWeight: 500 }}>
                    {agentNames[agent]}
                  </span>
                  {status.command && (
                    <code style={{ 
                      fontSize: '12px', 
                      color: '#6b7280',
                      background: '#f3f4f6',
                      padding: '2px 6px',
                      borderRadius: '3px'
                    }}>
                      {status.command}
                    </code>
                  )}
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {status.checking ? (
                    <>
                      <RefreshCw size={14} style={{ color: '#6b7280' }} className="spin" />
                      <span style={{ fontSize: '12px', color: '#6b7280' }}>Checking...</span>
                    </>
                  ) : status.checked ? (
                    status.installed ? (
                      <>
                        <Check size={16} style={{ color: '#10b981' }} />
                        <span style={{ fontSize: '12px', color: '#10b981', fontWeight: 500 }}>
                          Installed
                        </span>
                      </>
                    ) : (
                      <>
                        <AlertCircle size={16} style={{ color: '#f59e0b' }} />
                        <span style={{ fontSize: '12px', color: '#f59e0b', fontWeight: 500 }}>
                          Not Installed
                        </span>
                      </>
                    )
                  ) : (
                    <span style={{ fontSize: '12px', color: '#9ca3af' }}>Pending</span>
                  )}
                  
                  <button
                    onClick={() => checkAgentInstallation(agent)}
                    disabled={status.checking}
                    style={{
                      padding: '4px 8px',
                      fontSize: '11px',
                      background: 'transparent',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      cursor: status.checking ? 'not-allowed' : 'pointer',
                      color: '#6b7280'
                    }}
                    title="Recheck installation"
                  >
                    <RefreshCw size={12} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Installation instructions for selected agent */}
        {codingAgent && agentInstallationStatus[codingAgent].checked && 
         !agentInstallationStatus[codingAgent].installed && (
          <div style={{ 
            marginTop: '12px',
            padding: '12px',
            background: '#fef3c7',
            borderRadius: '4px',
            border: '1px solid #fbbf24',
            fontSize: '13px',
            color: '#92400e'
          }}>
            <strong>Warning: Selected agent not installed:</strong>
            <div style={{ marginTop: '8px' }}>
              {codingAgent === 'claude-code' && (
                <>
                  Install Claude Code CLI: <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '3px' }}>npm install -g @anthropic-ai/claude-code</code>
                </>
              )}
              {codingAgent === 'cursor' && (
                <>
                  Install Cursor Agent CLI: <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '3px' }}>npm install -g @cursor-ai/cursor-agent</code>
                </>
              )}
              {codingAgent === 'gemini' && (
                <>
                  Install Gemini CLI: <code style={{ background: '#fff', padding: '2px 6px', borderRadius: '3px' }}>npm install -g @google/gemini-cli</code>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
